import { useRef, useState } from "react";
import { Download, FilePdf, LockKey, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import { useIsStoreApp, useHasProAccess } from "@/lib/storeAppDetection";
import { track } from "@/lib/analytics";
import PawLinkQR, { renderBrandedQrCanvas } from "@/components/PawLinkQR";

// PawLink brand colors (RGB) — must match paw-forest / paw-terra / paw-bone
const BRAND_FOREST = [30, 58, 47];
const BRAND_TERRA = [224, 122, 95];
const BRAND_BONE = [246, 241, 234];
const BRAND_INK = [26, 32, 30];
const BRAND_INK2 = [110, 120, 116];

// Print-time paw-frame aspect ratio — matches PawLinkQR.jsx visual layout.
const PAW_ASPECT = 1;   // height / width — square QR (no paw frame)

/** Load an image (CORS-safe) to a HTMLImageElement */
function loadImg(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

export default function QRCard({ url, petName, petId, photoPath }) {
    const wrapRef = useRef(null);
    const { t } = useI18n();
    const { user } = useAuth();
    const isPremium = user?.plan === "premium";
    const isStoreApp = useIsStoreApp();
    // Early Access grants downloads on the Google Play Android build so
    // users can fully experience PawLink during launch without a paywall
    // dead-end. Backend Premium record is untouched.
    const hasAccess = useHasProAccess(user);
    const [busy, setBusy] = useState(null); // "png" | "pdf" | null

    const downloadPng = async () => {
        if (!hasAccess || busy) return;
        setBusy("png");
        try {
            const { dataUrl } = await renderBrandedQrCanvas(url, 1600, {
                avatarUrl: photoPath ? fileUrl(photoPath) : undefined,
            });
            const link = document.createElement("a");
            link.download = `pawlink-${(petName || petId || "pet").replace(/\s+/g, "-").toLowerCase()}.png`;
            link.href = dataUrl;
            document.body.appendChild(link); link.click(); link.remove();
            track("qr_downloaded", { format: "png", pet_id: petId, source: "qr_card", early_access: !isPremium && isStoreApp });
        } finally {
            setBusy(null);
        }
    };

    const downloadPdf = async () => {
        if (!hasAccess || busy) return;
        setBusy("pdf");
        try {
            const { dataUrl: qrData } = await renderBrandedQrCanvas(url, 1600, {
                avatarUrl: photoPath ? fileUrl(photoPath) : undefined,
            });

            // Try to load pet photo (if any) — safe to skip on error
            let petPhotoData = null;
            if (photoPath) {
                try {
                    const img = await loadImg(fileUrl(photoPath));
                    const c = document.createElement("canvas");
                    const s = 240;
                    c.width = s; c.height = s;
                    const cctx = c.getContext("2d");
                    cctx.fillStyle = "#FFFFFF";
                    cctx.fillRect(0, 0, s, s);
                    // cover-fit
                    const ratio = Math.max(s / img.width, s / img.height);
                    const w = img.width * ratio;
                    const h = img.height * ratio;
                    cctx.drawImage(img, (s - w) / 2, (s - h) / 2, w, h);
                    petPhotoData = c.toDataURL("image/jpeg", 0.82);
                } catch (_) { petPhotoData = null; }
            }

            const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
            const pageW = doc.internal.pageSize.getWidth();  // 210
            const pageH = doc.internal.pageSize.getHeight(); // 297
            const margin = 14;

            // === Outer decorative border ===
            doc.setDrawColor(...BRAND_FOREST);
            doc.setLineWidth(0.6);
            doc.roundedRect(margin, margin, pageW - margin * 2, pageH - margin * 2, 6, 6, "S");

            // Inner dashed border (subtle)
            doc.setDrawColor(...BRAND_TERRA);
            doc.setLineWidth(0.25);
            doc.setLineDashPattern([1.2, 1.2], 0);
            doc.roundedRect(margin + 3, margin + 3, pageW - (margin + 3) * 2, pageH - (margin + 3) * 2, 4, 4, "S");
            doc.setLineDashPattern([], 0);

            // === Brand header bar ===
            const headerY = margin + 8;
            const headerH = 22;
            doc.setFillColor(...BRAND_FOREST);
            doc.roundedRect(margin + 6, headerY, pageW - (margin + 6) * 2, headerH, 4, 4, "F");

            // Logo mark (paw circle)
            doc.setFillColor(...BRAND_TERRA);
            doc.circle(margin + 16, headerY + headerH / 2, 5, "F");
            doc.setFillColor(...BRAND_BONE);
            // simple paw dot cluster inside terra circle
            [[-1.8, -1.4], [1.8, -1.4], [-2.6, 0.4], [2.6, 0.4]].forEach(([dx, dy]) => {
                doc.circle(margin + 16 + dx, headerY + headerH / 2 + dy, 0.7, "F");
            });
            doc.ellipse(margin + 16, headerY + headerH / 2 + 1.7, 2.2, 1.4, "F");

            doc.setFont("helvetica", "bold");
            doc.setFontSize(18);
            doc.setTextColor(...BRAND_BONE);
            doc.text("PawLink", margin + 24, headerY + headerH / 2 + 1, { baseline: "middle" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(233, 220, 200);
            doc.text("Smart QR Pet ID", pageW - margin - 12, headerY + headerH / 2 + 1, { align: "right", baseline: "middle" });

            // === Headline ===
            let cursorY = headerY + headerH + 14;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(22);
            doc.setTextColor(...BRAND_INK);
            doc.text(t("qr.pdf_headline") || "If found, please scan", pageW / 2, cursorY, { align: "center" });

            // Pet name
            cursorY += 9;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(13);
            doc.setTextColor(...BRAND_TERRA);
            doc.text(petName ? `${petName}` : "Your pet", pageW / 2, cursorY, { align: "center" });

            // Optional pet photo (round-ish via clipping fake — just draw with rounded rect background)
            cursorY += 6;
            if (petPhotoData) {
                const photoSize = 24;
                const px = (pageW - photoSize) / 2;
                doc.setFillColor(255, 255, 255);
                doc.roundedRect(px - 1, cursorY - 1, photoSize + 2, photoSize + 2, 3, 3, "F");
                doc.setDrawColor(...BRAND_FOREST);
                doc.setLineWidth(0.4);
                doc.roundedRect(px - 1, cursorY - 1, photoSize + 2, photoSize + 2, 3, 3, "S");
                doc.addImage(petPhotoData, "JPEG", px, cursorY, photoSize, photoSize, undefined, "FAST");
                cursorY += photoSize + 6;
            } else {
                cursorY += 2;
            }

            // === QR block (paw-shaped premium QR) ===
            // The branded QR canvas is taller than wide (see PAW_ASPECT).
            const qrW = 92;
            const qrH = 92 * PAW_ASPECT; // ≈ 130
            const qrX = (pageW - qrW) / 2;
            const qrY = cursorY;

            // white QR frame with corner brackets
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(qrX - 6, qrY - 6, qrW + 12, qrH + 12, 4, 4, "F");
            doc.setDrawColor(...BRAND_FOREST);
            doc.setLineWidth(0.5);
            doc.roundedRect(qrX - 6, qrY - 6, qrW + 12, qrH + 12, 4, 4, "S");

            // Corner brackets (terra)
            doc.setDrawColor(...BRAND_TERRA);
            doc.setLineWidth(1.2);
            const b = 6; // bracket length
            const bx = qrX - 6, by = qrY - 6, bw = qrW + 12, bh = qrH + 12;
            // TL
            doc.line(bx, by + b, bx, by); doc.line(bx, by, bx + b, by);
            // TR
            doc.line(bx + bw - b, by, bx + bw, by); doc.line(bx + bw, by, bx + bw, by + b);
            // BL
            doc.line(bx, by + bh - b, bx, by + bh); doc.line(bx, by + bh, bx + b, by + bh);
            // BR
            doc.line(bx + bw - b, by + bh, bx + bw, by + bh); doc.line(bx + bw, by + bh - b, bx + bw, by + bh);

            doc.addImage(qrData, "PNG", qrX, qrY, qrW, qrH, undefined, "FAST");

            // SCAN HERE label
            const scanLabelY = qrY + qrH + 14;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...BRAND_TERRA);
            doc.text(t("qr.pdf_scan_here") || "SCAN HERE", pageW / 2, scanLabelY, { align: "center" });

            // Description
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(...BRAND_INK2);
            const description = t("qr.pdf_sub") || "This tag is protected by PawLink — scan to see this pet's photo, allergies, medical notes and contact the owner in one tap.";
            doc.text(description, pageW / 2, scanLabelY + 7, { align: "center", maxWidth: 150 });

            // URL
            const urlY = scanLabelY + 22;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(...BRAND_INK2);
            doc.text(t("qr.pdf_url_label") || "Or visit", pageW / 2, urlY, { align: "center" });
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...BRAND_FOREST);
            doc.text(url, pageW / 2, urlY + 6, { align: "center" });

            // Footer bar
            const footerH = 12;
            const footerY = pageH - margin - 6 - footerH;
            doc.setFillColor(...BRAND_BONE);
            doc.roundedRect(margin + 6, footerY, pageW - (margin + 6) * 2, footerH, 3, 3, "F");
            doc.setFont("helvetica", "italic");
            doc.setFontSize(9);
            doc.setTextColor(...BRAND_FOREST);
            doc.text(t("qr.pdf_footer") || "PawLink — Smart QR Pet ID · getpawlink.com", pageW / 2, footerY + footerH / 2 + 1, { align: "center", baseline: "middle" });

            doc.save(`pawlink-${(petName || petId || "pet").replace(/\s+/g, "-").toLowerCase()}.pdf`);
            track("qr_downloaded", { format: "pdf", pet_id: petId, source: "qr_card", early_access: !isPremium && isStoreApp });
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="flex flex-col items-center" data-testid="qr-card">
            <div ref={wrapRef} className="p-4 bg-white rounded-2xl border border-paw-line dark:border-white/10 shadow-soft">
                <PawLinkQR value={url} size={192} avatarUrl={photoPath ? fileUrl(photoPath) : undefined} data-testid="qr-card-image" />
            </div>

            <p className="mt-4 text-xs text-paw-ink2 dark:text-paw-bone/60 text-center max-w-[240px]">
                {t("qr.scan_hint")}
            </p>

            {hasAccess ? (
                <div className="mt-4 flex flex-wrap gap-2 justify-center" data-testid="qr-actions">
                    <button data-testid="qr-download-png" onClick={downloadPng} disabled={!!busy}
                        className="btn-outline !py-2 !px-4 text-sm disabled:opacity-60">
                        <Download size={16} />
                        <span className="ms-1.5">{busy === "png" ? t("qr.downloading") : t("qr.png")}</span>
                    </button>
                    <button data-testid="qr-download-pdf" onClick={downloadPdf} disabled={!!busy}
                        className="btn-primary !py-2 !px-4 text-sm disabled:opacity-60">
                        <FilePdf size={16} />
                        <span className="ms-1.5">{busy === "pdf" ? t("qr.downloading") : t("qr.pdf")}</span>
                    </button>
                </div>
            ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-paw-terra/40 bg-paw-terra/5 p-4 w-full text-center" data-testid="qr-locked">
                    <LockKey size={22} weight="duotone" className="mx-auto text-paw-terra" />
                    <p className="mt-2 font-medium text-paw-ink dark:text-paw-bone">{t("qr.locked_h")}</p>
                    <p className="mt-1 text-xs text-paw-ink2 dark:text-paw-bone/60">{t("qr.locked_sub")}</p>
                    {/* Google Play compliance: no purchase CTA inside the
                        store-distributed app. Web version still shows the
                        Unlock button that routes to /subscription. */}
                    {!isStoreApp && (
                        <Link to="/subscription" data-testid="qr-locked-cta" className="mt-3 inline-flex btn-primary !py-2 !px-4 text-sm">
                            <Sparkle size={14} weight="fill" className="text-white" />
                            <span className="ms-1.5">{t("qr.locked_cta")}</span>
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}
